import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useUser } from '../contexts/UserContext';
import { api } from '../services/api';
import './Checkout.css';
import { proxyBucketUrl, handleImageError } from '../utils/imageUtils';

interface ShippingInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
}

interface BillingInfo {
  sameAsShipping: boolean;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
}

interface PaymentInfo {
  method: 'card' | 'paypal' | 'bank' | 'cash';
}

const Checkout: React.FC = () => {
  const { state: cartState, getTotalItems, getTotalPrice, clearCart } = useCart();
  const { user, isAuthenticated } = useUser();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [awaitingPayment, setAwaitingPayment] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [shippingInfo, setShippingInfo] = useState<ShippingInfo>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: '',
    postalCode: '',
    country: 'Greece'
  });

  const [billingInfo, setBillingInfo] = useState<BillingInfo>({
    sameAsShipping: true,
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'Greece'
  });

  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo>({
    method: 'card'
  });

  // Redirect if cart is empty (but not if order was just completed)
  useEffect(() => {
    if (!cartState.loading && cartState.items.length === 0 && !orderCompleted) {
      navigate('/cart');
    }
  }, [cartState.items.length, cartState.loading, navigate, orderCompleted]);

  // If the browser restores this page from cache (e.g. the popup was blocked
  // and we fell back to a full-tab redirect), reset the state instead of
  // being stuck showing "Awaiting payment..." forever.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setAwaitingPayment(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Stop polling if the component unmounts mid-payment.
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Calculate totals
  const subtotal = getTotalPrice();
  const discount = subtotal * 0.05; // 5% vintage discount
  const shipping = 0; // Free shipping
  const total = subtotal - discount + shipping;

  const handleShippingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateShippingInfo()) {
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBillingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateBillingInfo()) {
      setCurrentStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const POLL_INTERVAL_MS = 2000;
  const MAX_POLL_ATTEMPTS = 150; // ~5 minutes

  const pollPaymentStatus = (orderNumber: string, popup: Window | null) => {
    let attempts = 0;

    pollIntervalRef.current = setInterval(async () => {
      attempts += 1;

      if (popup && popup.closed) {
        clearInterval(pollIntervalRef.current!);
        setAwaitingPayment(false);
        setError('Payment window was closed before completing payment. Please try again.');
        return;
      }

      if (attempts > MAX_POLL_ATTEMPTS) {
        clearInterval(pollIntervalRef.current!);
        setAwaitingPayment(false);
        setError('We could not confirm your payment in time. Please check your orders or try again.');
        return;
      }

      try {
        const { paymentStatus } = await api.confirmPayment(orderNumber);

        if (paymentStatus === 'AUTHORIZED') {
          clearInterval(pollIntervalRef.current!);
          if (popup && !popup.closed) popup.close();
          await clearCart();
          navigate(`/order-success/${orderNumber}`);
        } else if (paymentStatus === 'FAILED') {
          clearInterval(pollIntervalRef.current!);
          if (popup && !popup.closed) popup.close();
          setAwaitingPayment(false);
          setError('Payment was declined. Please try again.');
        }
        // otherwise still PENDING — keep polling
      } catch (err) {
        // Transient network hiccup — keep polling, MAX_POLL_ATTEMPTS bounds it.
        console.error('Payment status check failed:', err);
      }
    }, POLL_INTERVAL_MS);
  };

  const handleOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePaymentInfo()) return;

    setLoading(true);
    setError('');

    // Open the popup synchronously, before any `await`, so browsers don't
    // treat it as a blocked pop-up (it fills in the real URL once we have it).
    const popup = paymentInfo.method === 'card'
      ? window.open('', 'nbgpay-popup', 'width=500,height=720')
      : null;

    try {
      const orderData = {
        shippingInfo,
        billingInfo: billingInfo.sameAsShipping ? shippingInfo : billingInfo,
        paymentInfo,
        items: cartState.items,
        totals: {
          subtotal,
          discount,
          shipping,
          total
        }
      };

      const response = await api.createOrder(orderData);

      // Mark order as completed to prevent cart redirect
      setOrderCompleted(true);

      if (response.redirectUrl) {
        // Hosted Payment Page flow: keep the cart until payment is actually
        // confirmed (the customer may cancel the popup instead of paying).
        // It gets cleared once polling below sees it authorized.
        setAwaitingPayment(true);
        window.scrollTo({ top: 0 });

        if (popup) {
          popup.location.href = response.redirectUrl;
        } else {
          // Pop-up was blocked — fall back to a full-tab redirect.
          window.location.href = response.redirectUrl;
          return;
        }

        pollPaymentStatus(response.orderNumber, popup);
        return;
      }

      // No hosted-page redirect for this order (e.g. mock/cash/paypal/bank) —
      // close the pop-up if one was opened, nothing to wait for in it.
      if (popup) popup.close();

      // Clear cart after successful order
      await clearCart();

      // Redirect to success page
      const orderNumber = response.orderNumber;
      navigate(`/order-success/${orderNumber}`);
    } catch (err: any) {
      if (popup) popup.close();
      setError(err.message || 'Failed to place order');
      console.error('Order creation failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateShippingInfo = (): boolean => {
    const required = ['firstName', 'lastName', 'email', 'address', 'city', 'postalCode'];
    for (const field of required) {
      if (!shippingInfo[field as keyof ShippingInfo]) {
        setError(`${field.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`);
        return false;
      }
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(shippingInfo.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    setError('');
    return true;
  };

  const validateBillingInfo = (): boolean => {
    if (billingInfo.sameAsShipping) return true;
    
    const required = ['firstName', 'lastName', 'address', 'city', 'postalCode'];
    for (const field of required) {
      if (!billingInfo[field as keyof BillingInfo]) {
        setError(`Billing ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`);
        return false;
      }
    }
    
    setError('');
    return true;
  };

  const validatePaymentInfo = (): boolean => {
    // Card details are now entered on NBG Pay's own hosted page after redirect,
    // so there's nothing to validate here for the 'card' method.

    if (paymentInfo.method === 'cash') {
      // Validate that cash on delivery is available for the shipping country
      const allowedCountries = ['Greece', 'Cyprus'];
      if (!allowedCountries.includes(shippingInfo.country)) {
        setError('Cash on delivery is only available for Greece and Cyprus');
        return false;
      }
    }
    
    setError('');
    return true;
  };

  if (awaitingPayment) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-loading">
            <div className="loading-spinner"></div>
            <p>Waiting for you to complete payment in the pop-up window...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="checkout-page">
        <div className="checkout-auth-overlay">
          <div className="checkout-auth-modal">
            <button
              className="checkout-auth-close"
              onClick={() => navigate('/cart')}
              aria-label="Close"
            >
              ×
            </button>
            <h2>Sign In Required</h2>
            <p>You need an account to continue to checkout. Log in or create a free account to complete your purchase.</p>
            <div className="checkout-auth-actions">
              <button
                className="checkout-auth-btn primary"
                onClick={() => navigate('/login?redirect=/checkout')}
              >
                Log In
              </button>
              <button
                className="checkout-auth-btn secondary"
                onClick={() => navigate('/login?redirect=/checkout&mode=register')}
              >
                Create Account
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (cartState.loading) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-loading">
            <div className="loading-spinner"></div>
            <p>Loading checkout...</p>
          </div>
        </div>
      </div>
    );
  }

  if (cartState.items.length === 0) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <div className="checkout-header">
          <h1>Checkout</h1>
          <div className="checkout-steps">
            <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
              <span className="step-number">1</span>
              <span className="step-label">Shipping</span>
            </div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Billing</span>
            </div>
            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
              <span className="step-number">3</span>
              <span className="step-label">Payment</span>
            </div>
          </div>
        </div>

        <div className="checkout-content">
          <div className="checkout-main">
            {error && (
              <div className="checkout-error">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            {/* Step 1: Shipping Information */}
            {currentStep === 1 && (
              <div className="checkout-step">
                <h2>Shipping Information</h2>
                <form onSubmit={handleShippingSubmit} className="checkout-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        value={shippingInfo.firstName}
                        onChange={(e) => setShippingInfo({...shippingInfo, firstName: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        value={shippingInfo.lastName}
                        onChange={(e) => setShippingInfo({...shippingInfo, lastName: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={shippingInfo.email}
                      onChange={(e) => setShippingInfo({...shippingInfo, email: e.target.value})}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={shippingInfo.phone}
                      onChange={(e) => setShippingInfo({...shippingInfo, phone: e.target.value})}
                    />
                  </div>

                  <div className="form-group">
                    <label>Address *</label>
                    <input
                      type="text"
                      value={shippingInfo.address}
                      onChange={(e) => setShippingInfo({...shippingInfo, address: e.target.value})}
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>City *</label>
                      <input
                        type="text"
                        value={shippingInfo.city}
                        onChange={(e) => setShippingInfo({...shippingInfo, city: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Postal Code *</label>
                      <input
                        type="text"
                        value={shippingInfo.postalCode}
                        onChange={(e) => setShippingInfo({...shippingInfo, postalCode: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Country *</label>
                    <select
                      value={shippingInfo.country}
                      onChange={(e) => setShippingInfo({...shippingInfo, country: e.target.value})}
                      required
                    >
                      <option value="Greece">Greece</option>
                      <option value="Cyprus">Cyprus</option>
                      <option value="Germany">Germany</option>
                      <option value="France">France</option>
                      <option value="Italy">Italy</option>
                      <option value="Spain">Spain</option>
                    </select>
                  </div>

                  <div className="step-actions">
                    <button type="submit" className="continue-btn">
                      Continue to Billing
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Step 2: Billing Information */}
            {currentStep === 2 && (
              <div className="checkout-step">
                <h2>Billing Information</h2>
                <form onSubmit={handleBillingSubmit} className="checkout-form">
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={billingInfo.sameAsShipping}
                        onChange={(e) => setBillingInfo({...billingInfo, sameAsShipping: e.target.checked})}
                      />
                      Same as shipping address
                    </label>
                  </div>

                  {!billingInfo.sameAsShipping && (
                    <>
                      <div className="form-row">
                        <div className="form-group">
                          <label>First Name *</label>
                          <input
                            type="text"
                            value={billingInfo.firstName}
                            onChange={(e) => setBillingInfo({...billingInfo, firstName: e.target.value})}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Last Name *</label>
                          <input
                            type="text"
                            value={billingInfo.lastName}
                            onChange={(e) => setBillingInfo({...billingInfo, lastName: e.target.value})}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Address *</label>
                        <input
                          type="text"
                          value={billingInfo.address}
                          onChange={(e) => setBillingInfo({...billingInfo, address: e.target.value})}
                          required
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>City *</label>
                          <input
                            type="text"
                            value={billingInfo.city}
                            onChange={(e) => setBillingInfo({...billingInfo, city: e.target.value})}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Postal Code *</label>
                          <input
                            type="text"
                            value={billingInfo.postalCode}
                            onChange={(e) => setBillingInfo({...billingInfo, postalCode: e.target.value})}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Country *</label>
                        <select
                          value={billingInfo.country}
                          onChange={(e) => setBillingInfo({...billingInfo, country: e.target.value})}
                          required
                        >
                          <option value="Greece">Greece</option>
                          <option value="Cyprus">Cyprus</option>
                          <option value="Germany">Germany</option>
                          <option value="France">France</option>
                          <option value="Italy">Italy</option>
                          <option value="Spain">Spain</option>
                        </select>
                      </div>
                    </>
                  )}

                  <div className="step-actions">
                    <button 
                      type="button" 
                      onClick={() => {
                        setCurrentStep(1);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }} 
                      className="back-btn"
                    >
                      Back to Shipping
                    </button>
                    <button type="submit" className="continue-btn">
                      Continue to Payment
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Step 3: Payment Information */}
            {currentStep === 3 && (
              <div className="checkout-step">
                <h2>Payment Information</h2>
                <form onSubmit={handleOrderSubmit} className="checkout-form">
                  <div className="payment-methods">
                    <label className="payment-method">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="card"
                        checked={paymentInfo.method === 'card'}
                        onChange={(e) => setPaymentInfo({...paymentInfo, method: e.target.value as 'card'})}
                      />
                      <span className="method-icon">💳</span>
                      Credit/Debit Card
                    </label>
                    <label className="payment-method">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="paypal"
                        checked={paymentInfo.method === 'paypal'}
                        onChange={(e) => setPaymentInfo({...paymentInfo, method: e.target.value as 'paypal'})}
                      />
                      <span className="method-icon">📧</span>
                      PayPal
                    </label>
                    <label className="payment-method">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="bank"
                        checked={paymentInfo.method === 'bank'}
                        onChange={(e) => setPaymentInfo({...paymentInfo, method: e.target.value as 'bank'})}
                      />
                      <span className="method-icon">🏦</span>
                      Bank Transfer
                    </label>
                    <label className="payment-method">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cash"
                        checked={paymentInfo.method === 'cash'}
                        onChange={(e) => setPaymentInfo({...paymentInfo, method: e.target.value as 'cash'})}
                      />
                      <span className="method-icon">💵</span>
                      Cash on Delivery
                    </label>
                  </div>

                  {paymentInfo.method === 'card' && (
                    <div className="payment-info">
                      <p>🔒 You'll be redirected to our secure payment partner (NBG Pay) to enter your card details.</p>
                    </div>
                  )}

                  {paymentInfo.method === 'paypal' && (
                    <div className="payment-info">
                      <p>You will be redirected to PayPal to complete your payment.</p>
                    </div>
                  )}

                  {paymentInfo.method === 'bank' && (
                    <div className="payment-info">
                      <p>Bank transfer details will be provided after order confirmation.</p>
                    </div>
                  )}

                  {paymentInfo.method === 'cash' && (
                    <div className="payment-info">
                      <p>💵 <strong>Cash on Delivery</strong></p>
                      <p>You will pay in cash when your order is delivered to your address.</p>
                      <p>• Available for deliveries within Greece and Cyprus</p>
                      <p>• No additional fees for cash payment</p>
                      <p>• Please have the exact amount ready</p>
                    </div>
                  )}

                  <div className="step-actions">
                    <button 
                      type="button" 
                      onClick={() => {
                        setCurrentStep(2);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }} 
                      className="back-btn"
                    >
                      Back to Billing
                    </button>
                    <button 
                      type="submit" 
                      className="place-order-btn"
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : `Place Order - €${total.toFixed(2)}`}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="checkout-sidebar">
            <div className="order-summary">
              <h3>Order Summary</h3>
              
              <div className="order-items">
                {cartState.items.map(item => (
                  <div 
                    key={item.id} 
                    className="order-item clickable-product" 
                    onClick={() => navigate(`/product/${item.product?.id}`)}
                    title={`View ${item.product?.name}`}
                  >
                    <div className="item-image">
                      <img 
                        src={proxyBucketUrl(item.product?.images?.[0]) || '/products/placeholder.svg'} 
                        alt={item.product?.name || 'Product'} 
                        onError={handleImageError}
                      />
                    </div>
                    <div className="item-details">
                      <h4>{item.product?.name}</h4>
                      {item.size && <p className="item-size">Size: {item.size}</p>}
                      <p className="item-price">€{Number(item.product?.price || 0).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="order-totals">
                <div className="total-row">
                  <span>Subtotal ({getTotalItems()} {getTotalItems() === 1 ? 'item' : 'items'}):</span>
                  <span>€{subtotal.toFixed(2)}</span>
                </div>
                <div className="total-row">
                  <span>Shipping:</span>
                  <span className="free">Free</span>
                </div>
                <div className="total-row discount">
                  <span>Vintage collection discount:</span>
                  <span>-€{discount.toFixed(2)}</span>
                </div>
                <div className="total-divider"></div>
                <div className="total-row final">
                  <span>Total:</span>
                  <span>€{total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;